import type { Track } from './icy-parse';

/**
 * »Poišči skladbo« (P2 §3.12, §4.8) — **navaden iskalni naslov, brez API-ja**.
 *
 * Radio je odkrivanje: skladbo slišiš enkrat in nimaš kam po njo. Vsi
 * konkurenti tu odpovejo, ker rešitev vidijo kot integracijo — račun, žetoni,
 * dovoljenja. Ni je treba: vsaka od teh storitev ima javno iskalno stran, ki
 * sprejme niz v naslovu.
 *
 * ⚠️ **Zato tu ni ne ključa ne zahtevka ne odvisnosti.** Funkcija sestavi
 * naslov in nič drugega; ali je skladba tam, izve uporabnik, ko odpre povezavo.
 * Domena zato ostane čista in testabilna brez omrežja.
 */

export const SONG_SERVICES = ['apple', 'spotify', 'youtube'] as const;
export type SongService = (typeof SONG_SERVICES)[number];

/**
 * Iskalni niz iz skladbe: **izvajalec in naslov**, ločena s presledkom.
 *
 * Namerno **brez ločila** iz `player.track` (pomišljaj z presledki): to je
 * tipografija za bralca, iskalniku pa je dodaten znak šum. `raw` je zadnja
 * možnost — ICY brez razčlenjenega izvajalca ga vseeno nosi in je boljši od
 * praznega iskanja.
 */
export function songQuery(track: Track): string {
  const artist = track.artist ?? '';
  const parts = artist === '' ? [track.title] : [artist, track.title];
  const query = parts.join(' ').trim();
  return query === '' ? track.raw.trim() : query;
}

/**
 * Iskalni naslov storitve.
 *
 * `encodeURIComponent` in ne `URLSearchParams` pri Spotifyju: ta ima niz v
 * **poti**, ne v poizvedbi, in `URLSearchParams` bi presledek zakodiral v `+`,
 * kar Spotify v poti bere dobesedno.
 *
 * Vrne `null` za prazno iskanje — gumb, ki bi odprl prazno iskalno stran, je
 * slabši od gumba, ki ga ni.
 */
export function songSearchUrl(service: SongService, query: string): string | null {
  const trimmed = query.trim();
  if (trimmed === '') return null;

  switch (service) {
    case 'apple':
      return `https://music.apple.com/search?term=${encodeURIComponent(trimmed)}`;
    case 'spotify':
      return `https://open.spotify.com/search/${encodeURIComponent(trimmed)}`;
    case 'youtube':
      return `https://www.youtube.com/results?search_query=${encodeURIComponent(trimmed)}`;
  }
}
